import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import type { ResetPasswordResponse, TenantDetail as TenantDetailData } from "../../api/types";
import { DataTable } from "../../components/DataTable";

const PLAN_OPTIONS = ["starter", "professional", "business", "enterprise"];

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="stat-card">
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
    </div>
  );
}

export default function TenantDetail() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<TenantDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("");
  const [reset, setReset] = useState<ResetPasswordResponse | null>(null);

  function load() {
    if (!tenantId) return;
    setLoading(true);
    adminApi.tenants
      .get(tenantId)
      .then((d) => {
        setDetail(d);
        setPlan(d.tenant.planTier || "starter");
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load tenant"))
      .finally(() => setLoading(false));
  }

  useEffect(load, [tenantId]);

  async function toggleStatus() {
    if (!detail || !tenantId) return;
    setBusy(true);
    setError(null);
    try {
      if (detail.tenant.status === "SUSPENDED") {
        await adminApi.tenants.activate(tenantId);
      } else {
        await adminApi.tenants.suspend(tenantId);
      }
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update tenant status");
    } finally {
      setBusy(false);
    }
  }

  async function savePlan() {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.tenants.updatePlan(tenantId, plan);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update plan");
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await adminApi.tenants.resetOwnerPassword(tenantId);
      setReset(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setBusy(false);
    }
  }

  function copyReset() {
    if (reset) {
      navigator.clipboard
        ?.writeText(`Email: ${reset.email}\nPassword: ${reset.temporaryPassword}`)
        .catch(() => undefined);
    }
  }

  if (loading) return <p>Loading...</p>;
  if (error) return <div className="error-text">{error}</div>;
  if (!detail) return null;

  const { tenant } = detail;

  return (
    <div>
      <div className="page-header">
        <div>
          <button type="button" className="btn-secondary" onClick={() => navigate("/admin/tenants")}>
            &larr; Back to tenants
          </button>
          <h1 style={{ marginTop: 8 }}>{tenant.name}</h1>
          <p className="help-text">
            {tenant.slug} &middot;{" "}
            <span className={tenant.status === "ACTIVE" ? "badge badge-published" : "badge badge-archived"}>
              {tenant.status}
            </span>
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={toggleStatus} disabled={busy}>
          {tenant.status === "SUSPENDED" ? "Activate tenant" : "Suspend tenant"}
        </button>
      </div>

      {error && <div className="error-text">{error}</div>}

      <div className="stat-grid">
        <StatCard label="Users" value={detail.userCount} />
        <StatCard label="Reports" value={detail.reportCount} />
        <StatCard label="Executions (30d)" value={detail.executions30d} />
        <StatCard label="Last active" value={detail.lastActiveAt ? new Date(detail.lastActiveAt).toLocaleString() : "never"} />
      </div>

      <div className="page-panel">
        <h3>Plan</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={plan} onChange={(e) => setPlan(e.target.value)}>
            {PLAN_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </option>
            ))}
          </select>
          <button type="button" className="btn-primary" onClick={savePlan} disabled={busy || plan === tenant.planTier}>
            Save plan
          </button>
        </div>
      </div>

      <div className="page-panel">
        <h3>Owner credentials</h3>
        <p className="help-text">
          Reset the tenant owner's password if they've been locked out. This immediately replaces
          their current password.
        </p>
        <button type="button" className="btn-secondary" onClick={resetPassword} disabled={busy}>
          Reset owner password
        </button>

        {reset && (
          <div className="page-panel" style={{ borderColor: "#f59e0b", marginTop: 12 }}>
            <h4>Copy this password now</h4>
            <p className="help-text">This is the only time it's shown. Send it to the client directly.</p>
            <div className="code-block">
              Email: {reset.email}
              {"\n"}Password: {reset.temporaryPassword}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button type="button" className="btn-secondary" onClick={copyReset}>
                Copy
              </button>
              <button type="button" className="btn-secondary" onClick={() => setReset(null)}>
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="page-panel">
        <h3>Users</h3>
        <DataTable
          rows={detail.users}
          rowKey={(u) => u.id}
          emptyMessage="No users yet."
          columns={[
            { key: "email", header: "Email" },
            { key: "fullName", header: "Name" },
            { key: "roles", header: "Roles", render: (u) => u.roles.join(", ") },
            {
              key: "status",
              header: "Status",
              render: (u) => (
                <span className={u.status === "ACTIVE" ? "badge badge-published" : "badge badge-archived"}>
                  {u.status}
                </span>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
